import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Divider,
	Layout,
	ReferenceBadge,
	Richtext,
	Text,
	TextAlignment,
	TextTag,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { Checkmark, Close } from '@volkswagen-onehub/icons-core';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { OfertasContext } from '../../context/ofertas-context';
import { CustomBreakpoints } from '../../helpers/breakpoints';
import { checkActiveDisclaimer } from '../../helpers/get-disclaimer';
import {
	capitalizeSentence,
	formatPrecio,
	getConsumptionText,
	getDatosEconomicos,
	getEtiquetaDgt,
	getFinanciacionPeriodicidad,
	getPagoFinanciado,
} from '../../helpers/ofertas-helper';
import { useOfertasTracker } from '../../helpers/use-ofertas-tracker';
import { CarlineConfiguration } from '../../services/ofertas-service/models/calculator';
import { CarlineWithOfertas, DatoEconomico, Equipamiento, Oferta } from '../../services/ofertas-service/models/ofertas';
import { GRADIENT } from '../constant-styles';
import { RedirectCTAs } from '../redirect-cta';
import { ImageGallery } from './image-gallery';

const StickyBottomContainer = styled.div`
	background-image: ${GRADIENT.LAYER};
	bottom: 0px;
	position: sticky;

	@media screen and (max-width: 960px) {
		text-align: center;
	}

	@media screen and (min-width: 1200px) and (max-width: 1599px) {
		background-color: none;
	}

	@media screen and (min-width: 1200px) {
		z-index: -10;
	}
`;

const DesktopContainer = styled.div`
	position: relative;
	min-height: 100vh;
`;

const CloseDiv = styled.div`
	text-align: end;
	cursor: pointer;
`;

const ScrollContainer = styled.div`
	width: 100%;
	max-height: 100vh;
	overflow-y: auto;

	::-webkit-scrollbar {
		display: none; /* for Chrome, Safari and Opera */
		scrollbar-width: none; /* Firefox */
	}
`;

const PriceRow = styled.div`
	display: flex;
	align-items: flex-start;
	width: 100%;
	min-width: 0;

	// Super pequeño
	@media screen and (max-width: 359px) {
		display: block;
	}
`;

const PriceLeft = styled.div`
	min-width: 160px;
	// Super pequeño
	@media screen and (max-width: 359px) {
		width: 100%;
	}
`;

const PriceRight = styled.div`
	min-width: 160px;
	// Super pequeño
	@media screen and (max-width: 359px) {
		width: 100%;
	}
`;

const PriceDivider = styled.div`
	display: flex;
	align-self: stretch;
	margin: 0 ${(props) => props.theme.size.static300};

	@media screen and (max-width: 559px) {
		display: none;
	}
`;

const ScrollToSectionButtons = ({
	equipamientoRef,
	precioRef,
	galeriaRef,
}: {
	equipamientoRef: React.RefObject<HTMLDivElement>;
	precioRef: React.RefObject<HTMLDivElement>;
	galeriaRef: React.RefObject<HTMLDivElement>;
}): React.ReactElement => {
	const [selectedSection, setSelectedSection] = useState<string>('equipamiento');

	const handleSectionOnClick = (sectionName: string, sectionRef: React.RefObject<HTMLDivElement>) => {
		setSelectedSection(sectionName);

		if (sectionRef.current) {
			sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	return (
		<Container
			data-testid="oferta-layer__scroll-to-section__buttons"
			wrap={'never'}
			gutter={ContainerGutter.static250}
			padding={{ top: ContainerPadding.static400 }}
			stretchContent={{
				[Breakpoints.default]: true,
				[Breakpoints.b560]: false,
			}}
		>
			<CTA
				tag="a"
				emphasis={selectedSection === 'equipamiento' ? 'primary' : 'secondary'}
				size="small"
				onClick={() => handleSectionOnClick('equipamiento', equipamientoRef)}
			>
				Equipamiento
			</CTA>
			<CTA
				tag="a"
				emphasis={selectedSection === 'precio' ? 'primary' : 'secondary'}
				size="small"
				onClick={() => handleSectionOnClick('precio', precioRef)}
			>
				Precio
			</CTA>
			<CTA
				tag="a"
				emphasis={selectedSection === 'galeria' ? 'primary' : 'secondary'}
				size="small"
				onClick={() => handleSectionOnClick('galeria', galeriaRef)}
			>
				Galería
			</CTA>
		</Container>
	);
};

export const OfertaLayer = ({
	setIsLayerVisible,
	activeCarline,
	selectedOferta,
	layerRef,
	isCallMeNowOpen,
	handleScrollToDisclaimer,
}: {
	setIsLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	activeCarline: CarlineWithOfertas;
	layerRef?: React.MutableRefObject<LayerHandleV2<unknown> | null>;
	selectedOferta: Oferta;
	className: string;
	isCallMeNowOpen: boolean;
	handleScrollToDisclaimer: (reference: string) => void;
}): React.ReactElement => {
	const { ofertasService, disclaimerManager, serviceConfigProvider } = React.useContext(OfertasContext);

	const equipamientoRef = useRef<HTMLDivElement>(null);
	const precioRef = useRef<HTMLDivElement>(null);
	const galeriaRef = useRef<HTMLDivElement>(null);
	const [ofertaDataCarlineArray, setOfertaDataCarlineArray] = useState<CarlineConfiguration>();
	const { trackOfertasLayerLoad, trackOfertasLayerClose, trackOfertasLinkClick } = useOfertasTracker();

	const pagoFinanciado = getPagoFinanciado(selectedOferta.datosEconomicos);
	const selectedDatosEconomicos = getDatosEconomicos(selectedOferta.datosEconomicos);

	const leftDato = selectedDatosEconomicos?.[0];
	const rightDato = selectedDatosEconomicos?.[1];

	const PriceBlock = (datoEconomico: DatoEconomico | undefined) => {
		if (!datoEconomico) return null;

		return (
			<Container gutter={ContainerGutter.static100} wrap={'always'}>
				{/* DINAMIZADO EL PRECIO QUE APARECE */}
				<Container wrap={'auto'}>
					<Text tag={TextTag.span} appearance={TokenTextAppearance.bigcopy100} color={TokenTextColor.active} bold>
						{/* Modalidad solo aplicada al pago unico */}

						<Text appearance={TokenTextAppearance.headline200} bold={false}>
							{capitalizeSentence(datoEconomico.modalidad) + '\u00A0'}
						</Text>

						{/* Precio con el legal asociado */}
						<Text appearance={TokenTextAppearance.headline200} bold={true}>
							{datoEconomico.periodicidad == 'pago-unico'
								? formatPrecio(datoEconomico.precio) + '€'
								: parseFloat(datoEconomico?.precio.toFixed(2)).toString().replace('.', ',') +
									getFinanciacionPeriodicidad(selectedOferta.datosEconomicos)}{' '}
						</Text>

						<Text sup bold>
							{datoEconomico.legal && (
								<ReferenceBadge
									onClick={() => {
										const ref = checkActiveDisclaimer(
											disclaimerManager.getScopedDisclaimerManager().getFooterDisclaimers().reference,
											datoEconomico.legal,
										)?.references?.[0].replace('.', '');
										if (ref) handleScrollToDisclaimer(ref);
										handleCloseOnClick();
									}}
									aria-label="Ir al legal"
								>
									{checkActiveDisclaimer(
										disclaimerManager.getScopedDisclaimerManager().getFooterDisclaimers().reference,
										datoEconomico.legal,
									)?.references?.[0].replace('.', '')}
								</ReferenceBadge>
							)}
						</Text>

						{'\u00A0'}
						{/* Tipo de financiacion con enlace asociado */}
						<Text tag={TextTag.span} appearance={TokenTextAppearance.bigcopy100} color={TokenTextColor.active} bold>
							{datoEconomico.periodicidad != 'pago-unico' && (
								<>
									{datoEconomico.financiacion?.url ? (
										<CTA
											tag={'a'}
											emphasis={'tertiary'}
											href={datoEconomico.financiacion?.url}
											target={'_blank'}
											onClick={handleClickFinancialProduct}
										>
											{'con\u00A0'}
											<span style={{ textDecoration: 'underline' }}>{datoEconomico.financiacion?.tipo}</span>
										</CTA>
									) : (
										<>
											{'con\u00A0'}
											<span style={{ textDecoration: 'underline' }}>{datoEconomico.financiacion?.tipo}</span>
										</>
									)}
									{'\u00A0'}
								</>
							)}
						</Text>
					</Text>
				</Container>

				{/* Info financiacion */}
				<Container>
					{datoEconomico.periodicidad == 'pago-unico' ? (
						<Text tag={TextTag.span} appearance={TokenTextAppearance.copy100}>
							Sujeto a financiación lineal
						</Text>
					) : (
						<Text tag={TextTag.span} appearance={TokenTextAppearance.copy100} color={TokenTextColor.secondary}>
							<Richtext markdown={datoEconomico.financiacion?.detalle ?? ''} dangerouslyParseRawHTML />
						</Text>
					)}
				</Container>
			</Container>
		);
	};

	useEffect(() => {
		const subscription = ofertasService
			.getDataCalculator(selectedOferta.configuracion.marketingCode)
			.subscribe((dataCarlineArray) => {
				setOfertaDataCarlineArray(dataCarlineArray ?? undefined);
			});
		return () => subscription.unsubscribe();
	}, [ofertasService, selectedOferta.configuracion.marketingCode]);

	useEffect(() => {
		trackOfertasLayerLoad('Oferta Layer', selectedOferta, activeCarline);
	}, []);

	const handleCloseOnClick = () => {
		setIsLayerVisible(false);

		trackOfertasLayerClose('Close Oferta Layer', selectedOferta, activeCarline);

		if (layerRef?.current) {
			layerRef?.current.close();
			layerRef.current = null;
		}
	};

	const handleClickFinancialProduct = () => {
		const id = 'Click financial product';
		const contentId = `Oferta Layer - ${pagoFinanciado ? pagoFinanciado.financiacion?.tipo : ''}`;
		trackOfertasLinkClick(contentId, selectedOferta, activeCarline, id);
	};

	const renderInfoConsumoEmisiones = () => {
		const wltpInterpolations =
			ofertaDataCarlineArray?.configurations?.content?.consolidatedData?.wltp_data?.interpolations;

		if (!wltpInterpolations) {
			return null;
		}

		return (
			<Container
				padding={{
					left: {
						[Breakpoints.default]: ContainerPadding.grid001,
						[Breakpoints.b960]: ContainerPadding.static0,
					},
					right: {
						[Breakpoints.default]: ContainerPadding.grid001,
						[Breakpoints.b1600]: ContainerPadding.static0,
					},
				}}
				gutter={ContainerGutter.static200}
			>
				<Text appearance={TokenTextAppearance.copy100}>
					{getConsumptionText(wltpInterpolations, selectedOferta.configuracion.motorizacion.combustible)}
				</Text>
				<Text appearance={TokenTextAppearance.copy100}>
					{
						'1. La información relativa al consumo de combustible, las emisiones y la autonomía eléctrica ha sido determinada en conformidad a los procedimientos de medición contemplados por la normativa. Dichos datos no hacen referencia a un vehículo específico y no constituyen parte de la oferta. El equipamiento opcional puede afectar a valores relevantes de los vehículos, como el peso, la resistencia y la aerodinámica. Estos factores, junto con la climatología, las condiciones del tráfico y la forma de conducción, pueden afectar el consumo de combustible, el consumo de electricidad, las emisiones CO2 y las prestaciones del vehículo. Para más información sobre los valores oficiales de consumo de combustible y emisiones CO2 en los nuevos vehículos, puedes consultar la ‘Guía de Vehículos Turismo de venta en España, con indicación de consumos y emisiones de CO2’, que está disponible en todos los concesionarios y en la web del Instituto para la Diversificación y Ahorro de la Energía IDAE - El consumo de combustible, las emisiones de CO2 de un vehículo y la autonomía eléctrica , así como las discrepancias en los valores indicados, no dependen únicamente del uso eficaz del combustible por parte del vehículo y de otros factores técnicos (como las ruedas y los neumáticos utilizados), también dependen del tipo de conducción y de otros factores no técnicos. Los valores de emisiones de CO2 pueden variar en función del equipamiento seleccionado, implicando un cambio en el impuesto de matriculación.'
					}
				</Text>
			</Container>
		);
	};

	return (
		<>
			<DesktopContainer className="oferta-layer__desktop-container">
				<Layout
					data-testid="oferta-layer__layout-info"
					appearance={{
						[Breakpoints.default]: [
							{ name: '.', columns: 0 },
							{ name: 'a', columns: 23 },
							{ name: '.', columns: 1 },
							{ name: '.', columns: 0 },
							{ name: 'b', columns: 0 },
						],
						[Breakpoints.b560]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 22 },
							{ name: '.', columns: 1 },
							{ name: '.', columns: 0 },
							{ name: 'b', columns: 0 },
						],
						[Breakpoints.b960]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 10 },
							{ name: '.', columns: 1 },
							{ name: 'b', columns: 12 },
						],
						[CustomBreakpoints.DESKTOP]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 10 },
							{ name: '.', columns: 1 },
							{ name: 'b', columns: 12 },
						],
						[Breakpoints.b1600]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 10 },
							{ name: '.', columns: 13 },
						],
						[Breakpoints.b2560]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 10 },
							{ name: '.', columns: 13 },
						],
					}}
					allowOverflowingContent
				>
					<ScrollContainer className="oferta-layer__scroll-container">
						<Container
							wrap={'always'}
							padding={{
								top: ContainerPadding.dynamic0100,
								left: {
									[Breakpoints.default]: ContainerPadding.grid001,
									[Breakpoints.b560]: ContainerPadding.static0,
								},
							}}
							shrinkContent
						>
							{/* Titulo, descripcion y boton de cerrado en la layer */}
							<Container
								data-testid="oferta-layer__header header"
								wrap={'never'}
								horizontalAlign={'space-between'}
								verticalAlign={'flex-start'}
								shrinkContent
							>
								<Container data-testid="oferta-layer__header__name-etiqueta" wrap={'always'}>
									<Container wrap={'never'} gutter={ContainerGutter.static200} shrinkContent>
										<Text tag={TextTag.h1} appearance={TokenTextAppearance.headline400} bold>
											{activeCarline.nuevo ? 'Nuevo ' : null}
											{selectedOferta.carline?.salesgroup.title}
										</Text>
										{selectedOferta.configuracion.etiquetaDGT && (
											<Text>
												<img
													src={getEtiquetaDgt(selectedOferta.configuracion.etiquetaDGT)}
													width={32}
													alt={activeCarline.title}
												/>
											</Text>
										)}
									</Container>

									<Container shrinkContent>
										<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b1920}>
											<Text tag={TextTag.h3} appearance={TokenTextAppearance.copy100} bold>
												{selectedOferta.configuracion.motorizacion.name}
											</Text>
										</BreakpointWrapper>
										<BreakpointWrapper min={Breakpoints.b1920}>
											<Text tag={TextTag.h3} appearance={TokenTextAppearance.copy200} bold>
												{selectedOferta.configuracion.motorizacion.name}
											</Text>
										</BreakpointWrapper>
									</Container>
								</Container>

								<BreakpointWrapper min={Breakpoints.default}>
									<Text>
										<CloseDiv className="oferta-layer__close-button-container" onClick={handleCloseOnClick}>
											<Text textAlign={TextAlignment.end}>
												<Close />
											</Text>
										</CloseDiv>
									</Text>
								</BreakpointWrapper>
							</Container>
							{/* Botones de scroll para mobile */}
							<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
								<ScrollToSectionButtons
									equipamientoRef={equipamientoRef}
									precioRef={precioRef}
									galeriaRef={galeriaRef}
								/>
							</BreakpointWrapper>
							{/* Informacion del equipamiento destacado */}
							{selectedOferta.configuracion.equipamientos?.serie.length != 0 && (
								<Container
									data-testid="oferta-layer__info info"
									padding={{ top: ContainerPadding.static400 }}
									wrap={'always'}
									ref={equipamientoRef}
								>
									<Text tag={TextTag.h4} appearance={TokenTextAppearance.headline200} bold>
										Equipamiento destacado
									</Text>
									<Container
										data-testid="oferta-layer__info_check-list"
										padding={{ top: ContainerPadding.static250 }}
										wrap={'always'}
									>
										{selectedOferta.configuracion.equipamientos?.serie
											.slice(0, 7)
											.map((equipamiento: Equipamiento, index) => {
												return (
													<Container
														padding={{ bottom: ContainerPadding.static100 }}
														wrap={'never'}
														verticalAlign={'flex-start'}
														gutter={ContainerGutter.static200}
														shrinkContent
														key={index}
													>
														<Text tag={TextTag.span}>
															<Checkmark variant="small" />
														</Text>
														<BreakpointWrapper min={Breakpoints.default}>
															<Text tag={TextTag.span} hyphens="auto" appearance={TokenTextAppearance.copy100}>
																{equipamiento.equipamiento.titulo}
															</Text>
														</BreakpointWrapper>
													</Container>
												);
											})}
									</Container>
								</Container>
							)}

							{/* Precio del vehiculo */}
							<Container
								data-testid="oferta-layer__price price"
								padding={{
									top: ContainerPadding.static400,
									bottom: ContainerPadding.static400,
								}}
								wrap={'always'}
								shrinkContent
								ref={precioRef}
							>
								<Text tag={TextTag.h4} appearance={TokenTextAppearance.headline200} bold>
									Elige la opción que encaja contigo:
								</Text>

								<Container padding={{ top: ContainerPadding.static250 }}>
									{/* Layout para mobile (original) */}
									<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b560}>
										<PriceRow data-testid="oferta-layer__price__info">
											<PriceLeft>{PriceBlock(leftDato)}</PriceLeft>

											{rightDato && <PriceRight>{PriceBlock(rightDato)}</PriceRight>}
										</PriceRow>
									</BreakpointWrapper>

									{/* Layout para desktop (nuevo) */}
									<BreakpointWrapper min={Breakpoints.b560}>
										<PriceRow data-testid="oferta-layer__price__info">
											<PriceLeft>{PriceBlock(leftDato)}</PriceLeft>

											{rightDato && (
												<PriceDivider>
													<Divider isVertical />
												</PriceDivider>
											)}

											{rightDato && <PriceRight>{PriceBlock(rightDato)}</PriceRight>}
										</PriceRow>
									</BreakpointWrapper>
								</Container>

								<BreakpointWrapper min={Breakpoints.b960} max={CustomBreakpoints.DESKTOP}>
									<Container
										padding={{
											top: ContainerPadding.static400,
											bottom: ContainerPadding.static400,
										}}
										wrap={'never'}
										horizontalAlign={'flex-start'}
										verticalAlign={'center'}
										gutter={
											(serviceConfigProvider.configs['es-vwdata']?.customConfig as { brand?: string })?.brand == 'N'
												? ContainerGutter.dynamic0120
												: ContainerGutter.static0
										}
									>
										<RedirectCTAs
											copy={selectedOferta.calculatedFinance.rawResponse ? 'Pide esta oferta' : 'Pide presupuesto'}
											carline={activeCarline}
											emphasis="secondary"
											isMobile={true}
											oferta={selectedOferta}
											formTrigger={'es-oneform-v2'}
											trackingRef={'layer'}
										/>

										{(serviceConfigProvider.configs['es-vwdata']?.customConfig as { brand?: string })?.brand == 'N' &&
											isCallMeNowOpen && (
												<RedirectCTAs
													copy={'¿Te llamamos?'}
													carline={activeCarline}
													emphasis="primary"
													isMobile={true}
													oferta={selectedOferta}
													formTrigger="es-oneform-callme-embedded"
													trackingRef={'layer'}
												/>
											)}
									</Container>
									{renderInfoConsumoEmisiones()}
								</BreakpointWrapper>
								<BreakpointWrapper min={CustomBreakpoints.DESKTOP}>
									<Container
										padding={{
											top: ContainerPadding.static400,
											bottom: ContainerPadding.static400,
										}}
										wrap={'never'}
										horizontalAlign={'flex-start'}
										verticalAlign={'center'}
										gutter={
											(serviceConfigProvider.configs['es-vwdata']?.customConfig as { brand?: string })?.brand == 'N'
												? ContainerGutter.dynamic0120
												: ContainerGutter.static0
										}
									>
										<RedirectCTAs
											copy={selectedOferta.calculatedFinance.rawResponse ? 'Pide esta oferta' : 'Pide presupuesto'}
											carline={activeCarline}
											emphasis="secondary"
											isMobile={false}
											oferta={selectedOferta}
											formTrigger={'es-oneform-v2'}
											trackingRef={'layer'}
										/>

										{(serviceConfigProvider.configs['es-vwdata']?.customConfig as { brand?: string })?.brand == 'N' &&
											isCallMeNowOpen && (
												<RedirectCTAs
													copy={'¿Te llamamos?'}
													carline={activeCarline}
													emphasis="primary"
													isMobile={false}
													oferta={selectedOferta}
													formTrigger="es-oneform-callme-embedded"
													trackingRef={'layer'}
												/>
											)}

										{/* <CTA emphasis="primary" tag="button" size="small">
											Consíguelo online
										</CTA> */}
									</Container>
									{renderInfoConsumoEmisiones()}
								</BreakpointWrapper>
							</Container>
						</Container>
					</ScrollContainer>

					<Container>
						{selectedOferta.galeria && (
							<BreakpointWrapper min={CustomBreakpoints.DESKTOP}>
								<ImageGallery oferta={selectedOferta} carline={activeCarline} />
							</BreakpointWrapper>
						)}
					</Container>
				</Layout>

				{selectedOferta.galeria && (
					<BreakpointWrapper min={Breakpoints.default} max={CustomBreakpoints.DESKTOP}>
						<Layout
							data-testid="oferta-layer__layout-slider"
							appearance={{
								[Breakpoints.default]: [{ name: 'a', columns: 24 }],
							}}
							allowOverflowingContent
						>
							<ImageGallery oferta={selectedOferta} carline={activeCarline} />
						</Layout>
					</BreakpointWrapper>
				)}
			</DesktopContainer>

			<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
				<StickyBottomContainer className="oferta-layer__sticky-bottom-container">
					<Layout
						data-testid="oferta-layer__layout-cta-mobile"
						appearance={{
							[Breakpoints.default]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b960]: [
								{ name: '.', columns: 2 },
								{ name: 'a', columns: 20 },
								{ name: '.', columns: 2 },
							],
							[Breakpoints.b1600]: [
								{ name: '.', columns: 2 },
								{ name: 'a', columns: 8 },
								{ name: '.', columns: 14 },
							],
							[Breakpoints.b2560]: [
								{ name: '.', columns: 3 },
								{ name: 'a', columns: 6 },
								{ name: '.', columns: 3 },
								{ name: 'b', columns: 12 },
							],
						}}
						allowOverflowingContent
					>
						<Container
							data-testid="oferta-layer__cta-container"
							padding={{
								top: ContainerPadding.static250,
								bottom: ContainerPadding.dynamic0100,
							}}
							verticalAlign={'center'}
							horizontalAlign={{ [Breakpoints.default]: 'center' }}
							wrap={'always'}
							shrinkContent
							gutter={
								(
									serviceConfigProvider.configs['es-vwdata']?.customConfig as {
										brand?: string;
									}
								)?.brand == 'N'
									? ContainerGutter.static200
									: ContainerGutter.static0
							}
						>
							<RedirectCTAs
								copy={'Pide presupuesto'}
								carline={activeCarline}
								emphasis="secondary"
								isMobile={true}
								oferta={selectedOferta}
								formTrigger={'es-oneform-v2'}
								trackingRef={'layer'}
							/>

							{(
								serviceConfigProvider.configs['es-vwdata']?.customConfig as {
									brand?: string;
								}
							)?.brand == 'N' &&
								isCallMeNowOpen && (
									<RedirectCTAs
										copy={'¿Te llamamos?'}
										carline={activeCarline}
										emphasis="primary"
										isMobile={true}
										oferta={selectedOferta}
										formTrigger="es-oneform-callme-embedded"
										trackingRef={'layer'}
									/>
								)}

							{/* <CTA
								tag="button"
								emphasis="primary"
								size="large"
								stretchContent={{
									[Breakpoints.default]: true,
									[Breakpoints.b560]: false,
								}}
							>
								Consíguelo online
							</CTA> */}
						</Container>
					</Layout>
				</StickyBottomContainer>
				<Container
					padding={{
						top: ContainerPadding.static250,
						bottom: ContainerPadding.dynamic0100,
					}}
					verticalAlign={'center'}
					horizontalAlign={'center'}
					shrinkContent
				>
					{renderInfoConsumoEmisiones()}
				</Container>
			</BreakpointWrapper>
		</>
	);
};
